import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {PlatformService} from '../../@core/utils/platform.service';

type SidebarState = 'expanded' | 'compacted' | 'collapsed';

@Injectable({providedIn: 'root'})
export class AppSidebarService {
	private readonly mobileBreakpoint = 992;
	private readonly states = new Map<string, BehaviorSubject<SidebarState>>();
	private readonly hoverPeek = new BehaviorSubject<boolean>(false);
	private wasMobileViewport = typeof window !== 'undefined' && window.innerWidth < 992;
	private wasTabletViewport = typeof window !== 'undefined' && this.isTabletSidebarViewport();

	constructor(private readonly platformService: PlatformService) {
		if (typeof window !== 'undefined') {
			window.addEventListener('resize', () => this.syncStateForViewport());
		}
	}

	isNativeApp(): boolean {
		return this.platformService.isMobileApp();
	}

	/** Sidebar en overlay plein écran (mobile web ou app Capacitor). */
	isOverlaySidebarMode(): boolean {
		return this.isNativeApp() || this.isMobileViewport() || this.isTabletSidebarViewport();
	}

	isCollapsedOverlayMode(): boolean {
		return this.isNativeApp() || this.isMobileViewport();
	}

	isMobileViewport(): boolean {
		return typeof window !== 'undefined' && window.innerWidth < this.mobileBreakpoint;
	}

	isCoarsePointer(): boolean {
		return typeof window !== 'undefined'
			&& window.matchMedia('(hover: none) and (pointer: coarse)').matches;
	}

	/** iPad / tablette tactile en paysage (≥ 992px) : barre icônes + expansion au tap. */
	isTabletSidebarViewport(): boolean {
		return !this.isMobileViewport() && this.isCoarsePointer();
	}

	usesOverlayExpand(): boolean {
		return this.isOverlaySidebarMode();
	}

	getHoverPeek(): Observable<boolean> {
		return this.hoverPeek.asObservable();
	}

	setHoverPeek(active: boolean): void {
		if (this.isMobileViewport() || this.isTabletSidebarViewport()) {
			active = false;
		}
		if (this.hoverPeek.value !== active) {
			this.hoverPeek.next(active);
		}
	}

	getSidebarState(tag = 'menu-sidebar'): Observable<SidebarState> {
		return this.getSubject(tag).asObservable();
	}

	toggle(_compact = true, tag = 'menu-sidebar'): void {
		const subject = this.getSubject(tag);
		if (this.isCollapsedOverlayMode()) {
			subject.next(subject.value === 'collapsed' ? 'expanded' : 'collapsed');
			return;
		}
		if (this.isTabletSidebarViewport()) {
			subject.next(subject.value === 'compacted' ? 'expanded' : 'compacted');
			return;
		}
		subject.next(subject.value === 'expanded' ? 'compacted' : 'expanded');
	}

	compact(tag = 'menu-sidebar'): void {
		if (this.isCollapsedOverlayMode()) {
			this.collapse(tag);
			return;
		}
		this.getSubject(tag).next('compacted');
	}

	collapse(tag = 'menu-sidebar'): void {
		this.getSubject(tag).next('collapsed');
	}

	expand(tag = 'menu-sidebar'): void {
		this.getSubject(tag).next('expanded');
	}

	collapseExpandedOverlay(tag = 'menu-sidebar'): void {
		if (this.isCollapsedOverlayMode()) {
			this.collapse(tag);
			return;
		}
		if (this.isTabletSidebarViewport()) {
			this.compact(tag);
			return;
		}
		this.compact(tag);
	}

	private getSubject(tag: string): BehaviorSubject<SidebarState> {
		if (!this.states.has(tag)) {
			this.states.set(tag, new BehaviorSubject<SidebarState>(this.getInitialState()));
		}
		return this.states.get(tag);
	}

	private getInitialState(): SidebarState {
		if (this.isCollapsedOverlayMode()) {
			return 'collapsed';
		}
		if (this.isTabletSidebarViewport()) {
			return 'compacted';
		}
		return 'expanded';
	}

	private syncStateForViewport(): void {
		if (this.isNativeApp()) {
			return;
		}

		const isMobile = this.isMobileViewport();
		const isTablet = this.isTabletSidebarViewport();

		if (isMobile === this.wasMobileViewport && isTablet === this.wasTabletViewport) {
			return;
		}
		this.wasMobileViewport = isMobile;
		this.wasTabletViewport = isTablet;

		this.setHoverPeek(false);

		for (const subject of this.states.values()) {
			if (isMobile) {
				if (subject.value !== 'collapsed') {
					subject.next('collapsed');
				}
				continue;
			}
			if (isTablet) {
				if (subject.value === 'collapsed' || subject.value === 'expanded') {
					subject.next('compacted');
				}
				continue;
			}
			if (subject.value === 'collapsed') {
				subject.next('expanded');
			}
		}
	}
}
